import Box from "@material-ui/core/Box";
import { makeStyles } from "@material-ui/core/styles";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import React from "react";
import { carecentreTypeScale as type } from "../../typeScale";
import { cx } from "../utils";

const zen = {
  primary: "#0765FF",
  primarySoft: "#DCE8FF",
  bgBone: "#EFEEE9",
  paper: "#FFFFFF",
  text: "#15191F",
  muted: "#797160",
  line: "#E3E1DB",
  terra: "#15191F",
  terraSoft: "#EFEEE9",
};

const useStyles = makeStyles(() => ({
  root: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 12,
    maxWidth: "100%",
    flexWrap: "wrap",
  },
  group: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    minHeight: 48,
    padding: 4,
    background: zen.bgBone,
    border: `1px solid ${zen.line}`,
    borderRadius: 3,
    boxSizing: "border-box",
    boxShadow: "none",
    "& .MuiToggleButtonGroup-grouped": {
      margin: 0,
      border: "none",
      borderRadius: 3,
    },
  },
  button: {
    minHeight: 36,
    padding: "8px 14px",
    textTransform: "none",
    fontFamily:
      "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontWeight: 600,
    fontSize: type.body.fontSize,
    lineHeight: `${type.body.lineHeight}px`,
    border: "none !important",
    borderRadius: "3px !important",
    color: zen.muted,
    background: "transparent",
    transition: "background-color 120ms ease, color 120ms ease",
    "&:hover": {
      background: "rgba(255, 255, 255, 0.55)",
      color: zen.text,
    },
    "&.Mui-selected": {
      backgroundColor: zen.paper,
      color: zen.text,
      boxShadow: "0 1px 2px rgba(15, 37, 71, 0.06)",
      "&:hover": {
        backgroundColor: zen.paper,
      },
    },
  },
  itemContent: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    whiteSpace: "nowrap",
  },
  dot: {
    width: 9,
    height: 9,
    borderRadius: "50%",
    background: zen.primary,
    flexShrink: 0,
  },
  count: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 24,
    height: 22,
    padding: "0 7px",
    borderRadius: 4,
    background: zen.primarySoft,
    color: zen.primary,
    fontSize: type.caption.fontSize,
    fontWeight: 700,
    lineHeight: `${type.caption.lineHeight}px`,
    boxSizing: "border-box",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 30,
    height: 24,
    padding: "0 8px",
    borderRadius: 4,
    background: zen.terraSoft,
    color: zen.terra,
    fontSize: type.caption.fontSize,
    fontWeight: 700,
    lineHeight: `${type.caption.lineHeight}px`,
    boxSizing: "border-box",
  },
  actions: {
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
  },
  "@media (max-width: 760px)": {
    root: {
      alignItems: "stretch",
      flexDirection: "column",
      gap: 10,
      width: "100%",
    },
    group: {
      width: "100%",
      overflowX: "auto",
    },
  },
}));

/**
 * Compact sub-navigation — MUI ToggleButtonGroup.
 */
const CcSubTabs = ({
  items = [],
  activeKey,
  onChange,
  className,
  actions,
  ariaLabel = "Section tabs",
}) => {
  const classes = useStyles();

  const handleChange = (_, key) => {
    if (!key) {
      return;
    }
    const item = items.find((entry) => entry.key === key);
    if (item) {
      onChange?.(item);
    }
  };

  return (
    <Box className={[classes.root, className].filter(Boolean).join(" ")}>
      <ToggleButtonGroup
        aria-label={ariaLabel}
        className={classes.group}
        exclusive
        size="small"
        value={activeKey}
        onChange={handleChange}
      >
        {items.map((item) => {
          const isActive = activeKey === item.key;
          const showDot =
            item.dot !== false &&
            item.dotTone &&
            (item.alwaysShowDot ||
              (isActive &&
                (item.dotTone === "active" || item.dotTone === undefined)));

          return (
            <ToggleButton
              className={cx(
                classes.button,
                item.tabVariant && `cc-sub-tabs__btn--${item.tabVariant}`,
                isActive &&
                  item.tabVariant &&
                  "cc-sub-tabs__btn--variant-active",
              )}
              key={item.key}
              value={item.key}
            >
              <Box className={classes.itemContent}>
                {showDot ? (
                  <span
                    className={cx(
                      classes.dot,
                      item.dotTone &&
                        item.dotTone !== "active" &&
                        `cc-sub-tabs__dot--${item.dotTone}`,
                    )}
                  />
                ) : null}
                {item.badge ? (
                  <span className={classes.badge}>{item.badge}</span>
                ) : null}
                <span>{item.label}</span>
                {item.count !== undefined ? (
                  <span className={classes.count}>{item.count}</span>
                ) : null}
              </Box>
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>
      {actions ? <Box className={classes.actions}>{actions}</Box> : null}
    </Box>
  );
};

export default CcSubTabs;
